import { createSlice } from "@reduxjs/toolkit";

const MAX_ITEMS = 100; // optional: list cap

const initialState = {
  items: [],       // [{ _id, name, action, time, avatar, unread, url }]
  unreadCount: 0,  // fast badge calc
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Replace feed from server (and set unread counter)
    setFeed(state, action) {
      const items = Array.isArray(action.payload?.items) ? action.payload.items : [];
      const unreadCount =
        typeof action.payload?.unreadCount === "number"
          ? action.payload.unreadCount
          : items.filter((x) => x.unread).length;

      state.items = items.slice(0, MAX_ITEMS);
      state.unreadCount = unreadCount;
    },

    // Add a single notif (from Socket.io or SW postMessage)
    addOne(state, action) {
      const n = action.payload;
      if (!n) return;
      const id = n._id || n.notifId;
      if (!id) return;

      const idx = state.items.findIndex((x) => x._id === id);
      if (idx === -1) {
        state.items.unshift({ ...n, _id: id });
        if (n.unread !== false) state.unreadCount += 1;
        if (state.items.length > MAX_ITEMS) state.items.pop();
      } else {
        // update existing (preserve read flag if already read)
        const prev = state.items[idx];
        const merged = { ...prev, ...n, _id: id, unread: prev.unread && n.unread !== false };
        state.items[idx] = merged;
      }
    },

    // Mark given ids as read (local UI update)
    markReadLocal(state, action) {
      const ids = new Set(action.payload || []);
      let newUnread = 0;
      state.items = state.items.map((it) => {
        if (ids.has(it._id)) return { ...it, unread: false };
        if (it.unread) newUnread += 1;
        return it;
      });
      // recount (or just recalc after map)
      state.unreadCount = state.items.filter((x) => x.unread).length;
    },

    // Mark all as read (local UI update)
    markAllReadLocal(state) {
      state.items = state.items.map((it) => ({ ...it, unread: false }));
      state.unreadCount = 0;
    },

    // Optional helpers
    removeOne(state, action) {
      const id = action.payload;
      const wasUnread = state.items.find((x) => x._id === id)?.unread;
      state.items = state.items.filter((x) => x._id !== id);
      if (wasUnread) state.unreadCount = Math.max(0, state.unreadCount - 1);
    },
    clearAll(state) {
      state.items = [];
      state.unreadCount = 0;
    },
  },
});

export const {
  setFeed,
  addOne,
  markReadLocal,
  markAllReadLocal,
  removeOne,
  clearAll,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;

// Selectors (optional)
export const selectNotifications = (s) => s.notifications?.items || [];
export const selectUnreadCount = (s) => s.notifications?.unreadCount || 0;
